export default class Booking {
    /**
     * Create new booking.
     */
    constructor() {
        this.lines = [];
        this.send_sms = false;
        this.preferred_zone = null;
    }

    /**
     * Get lines with quantity.
     *
     * @return {Object[]}
     */
    get linesWithQuantity() {
        let lines = [];

        for (let line of this.lines) {
            if (+line.quantity <= 0) {
                continue;
            }

            lines.push(line);
        }

        return lines;
    }

    /**
     * Get total quantity of lockers for this booking.
     *
     * @return {Number}
     */
    get totalLockers() {
        let total = 0;
        for (let line of this.lines) {
            total += +line.quantity;
        }

        return total;
    }

    /**
     * Calculate total amount.
     *
     * @param {Object} event
     * @return Number
     */
    calculateTotalAmount(event = null) {
        if (this.token) {
            // Existing booking, use amount from API
            return this.amount;
        }

        if (this.linesWithQuantity.length === 0) {
            return 0;
        }

        let amount = 0;
        if (event && !event.fees_including) {
            amount = this.getTotalFees(event);
        }

        for (let line of this.linesWithQuantity) {
            amount += line.quantity * line.price;
        }

        if (this.send_sms && event) {
            amount += event.sms_price;
        }

        return amount;
    }

    /**
     * Get the total fees including VAT.
     * - Application fee
     * - Transaction costs
     * @param event
     * @returns {Number}
     */
    getTotalFees(event) {
        const totalWithoutFees = this.getTotalPriceWithoutFees();
        if (totalWithoutFees === 0) {
            return 0;
        }

        return this.getApplicationFees(event, totalWithoutFees)
            + this.getTransactionsCosts(event, totalWithoutFees);
    }

    /**
     * Round a number to two decimals.
     *
     * @param number
     * @returns {number}
     */
    roundToTwoDecimals(number) {
        return Math.round(number * 100) / 100;
    }

    /**
     * Get the total price without fees.
     * - Quantity * price of each line
     * @returns {Number}
     */
    getTotalPriceWithoutFees() {
        let amount = 0;

        for (let line of this.linesWithQuantity) {
            amount += line.quantity * line.price;
        }

        return amount;
    }

    /**
     * Get the transaction costs including VAT.
     *
     * @param event
     * @param totalWithoutFees
     * @returns {Number}
     */
    getTransactionsCosts(event, totalWithoutFees) {
        const fromPercentage = this.getFeeFromPercentage(event.transaction_costs_percentage, totalWithoutFees);
        let transactionCosts = Math.max(event.minimum_transaction_costs, fromPercentage);

        if (!event.fees_include_vat) {
            transactionCosts = transactionCosts * (1 + event.vat_percentage);
        }

        return this.roundToTwoDecimals(transactionCosts);
    }

    /**
     * Get application fees including VAT.
     *
     * @param event
     * @param totalWithoutFees
     * @returns {Number}
     */
    getApplicationFees(event, totalWithoutFees) {
        const fromPercentage = this.getFeeFromPercentage(event.application_fee_percentage, totalWithoutFees);
        let fee = Math.max(event.minimum_application_fee, fromPercentage);

        if (!event.fees_include_vat) {
            fee = fee * (1 + event.vat_percentage);
        }

        return this.roundToTwoDecimals(fee);
    }

    /**
     * Get the fee from minimum percentage.
     * @param percentage
     * @param totalWithoutFees
     * @returns {Number}
     */
    getFeeFromPercentage(percentage, totalWithoutFees) {
        if (totalWithoutFees === 0) {
            return 0;
        }

        return (percentage / 100) * totalWithoutFees;
    }

    /**
     * Total quantity of lines.
     * @returns {Number}
     */
    totalQuantity(){
        let total = 0;

        for (let line of this.linesWithQuantity) {
            total += line.quantity;
        }

        return total;
    }

    /**
     * Is this booking free?
     * @returns {boolean}
     */
    isFree() {
        return this.calculateTotalAmount() === 0;
    }

    /**
     * Set lines for the locker sizes of the period.
     *
     * @param {Object[]} lockerSizes
     * @param {Object[]} prices
     */
    setLinesForLockerSizes(lockerSizes, prices) {
        this.lines = [];

        for (let lockerSize of lockerSizes) {
            let price = prices.find(p => p.id === lockerSize.id);

            if (typeof price === 'undefined') {
                continue;
            }

            this.lines.push({
                'locker_size_id': lockerSize.id,
                'quantity': 0,
                'name': lockerSize.name,
                'description': lockerSize.description,
                'price': price.price,
                'available': price.available
            });
        }
    }

    /**
     * Reset period and quantities of this booking.
     */
    reset() {
        this.period = null;
        this.period_id = null;

        for (let line of this.lines) {
            line.quantity = 0;
        }
    }
}
